import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  useReactTable, getCoreRowModel, getSortedRowModel,
  flexRender, createColumnHelper,
} from '@tanstack/react-table';
import {
  Trash2, ImageIcon, Package, ChevronUp, ChevronDown, Plus,
  ArrowUp, ArrowDown, ArrowUpDown, SlidersHorizontal, RotateCcw, X, EyeOff
} from 'lucide-react';
import { rectSortingStrategy } from '@dnd-kit/sortable';
import { useSelector } from 'react-redux';
import Apiurl from '../../Helpers/apiUrl';
import InventoryPopUp from '../utils/InventoryPopUp';
import SmartAutoRecommendations from './SmartAutoRecommendations';
import StockLable from '../utils/StockLable';
import { GripVertical } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { Tooltip } from '@mui/material';
import { CSS } from '@dnd-kit/utilities';
import { formatCurrency, getSelectedCurrency, getCurrencySymbol, convertAmount } from '../../Helpers/currencyHelper';

const COL_W = {
  drag: 70,
  select: 120,
  productCode: 180,
  area: 130,
  brand: 120,
  description: 200,
  collection: 120,
  productType: 105,
  category: 110,
  priceSegment: 105,
  finish: 130,
  image: 56,
  linediagram_image: 56,
  quantity: 170,
  mrp: 150,
  basePrice: 170,
  discountPercent: 140,
  discountPrice: 140,
  gstRate: 80,
  totalDiscount: 140,
  total: 160,
  preferences: 120,
  stock: 80,
  stockIndicator: 150,
  pruning_date: 150,
  status: 120,
};

const TOGGLEABLE_COLUMNS = [
  { id: 'productCode', label: 'Product Code' },
  { id: 'area', label: 'Area' },
  { id: 'brand', label: 'Brand' },
  { id: 'description', label: 'Description' },
  { id: 'image', label: 'Image' },
  { id: 'linediagram_image', label: 'Line Diagram' },
  { id: 'collection', label: 'Collection' },
  { id: 'category', label: 'Category' },
  { id: 'priceSegment', label: 'Price Segment' },
  { id: 'finish', label: 'Finish' },
  { id: 'quantity', label: 'Qty' },
  { id: 'mrp', label: 'MRP' },
  { id: 'basePrice', label: 'Base Price' },
  { id: 'discountPercent', label: 'Disc %' },
  { id: 'discountPrice', label: 'Disc Price' },
  { id: 'gstRate', label: 'GST %' },
  { id: 'totalDiscount', label: 'Total Disc' },
  { id: 'total', label: 'Total' },
  { id: 'preferences', label: 'Preferences' },
  { id: 'stock', label: 'Stock' },
  { id: 'stockIndicator', label: 'Stock Indicator' },
  { id: 'status', label: 'Status' },
  { id: 'pruning_date', label: 'Pruning Date' },
];

const DEFAULT_COLUMN_ORDER = [
  'select',
  'productCode',
  'area',
  'brand',
  'description',
  'image',
  'linediagram_image',
  'collection',
  'category',
  'priceSegment',
  'finish',
  'quantity',
  'mrp',
  'basePrice',
  'discountPercent',
  'discountPrice',
  'gstRate',
  'totalDiscount',
  'total',
  'preferences',
  'stock',
  'stockIndicator',
  'status',
  'pruning_date',
];

const R2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const fmt = (n, dec = 2) => R2(n).toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec });

const mapProduct = (p) => ({
  productCode: p.sku_code || '',
  brand: p.brand || '',
  description: p.product_desc || '',
  collection: p.collection || '',
  productType: p.product_typ_group || '',
  category: p.category || '',
  priceSegment: p.priceSegment || '',
  finish: p.finish || '',
  image: p.image || null,
  linediagram_image: p.linediagram_image || null,
  mrp: Number(p.mrp) || 0,
  basePrice: Number(p.basePrice) || 0,
  gstRate: Number(p.gst) || 18,
  preferences: p.preferences || '',
  stock: Number(p.stockAvailable) ?? null,
  stockIndicator: p.stockIndicator || '',
  pruning_date: p.pruning_date || '',
  status: p.status || p.product_status || '',
});

const AREAS = ['Shower', 'Basin', 'EWC', 'Kitchen', 'Faucet'];

function SortIcon({ state }) {
  if (state === 'desc') return <ArrowDown size={12} className="text-blue-500 ml-1 inline" />;
  if (state === 'asc') return <ArrowUp size={12} className="text-blue-500 ml-1 inline" />;
  return <ArrowUpDown size={11} className="text-slate-300 ml-1 inline" />;
}

function CodeInput({ value, apiId, onSelect, onProductCodeChange, products = [] }) {
  const [q, setQ] = useState(value || '');
  const prevValueRef = useRef(value || '');

  useEffect(() => {
    setQ(value || '');
    prevValueRef.current = value || '';
  }, [value]);

  useEffect(() => {
    if (!q) return;
    const timer = setTimeout(() => {
      const match = products.find(p => (p.sku_code || '').toLowerCase() === q.toLowerCase());
      if (match) {
        const newCode = match.sku_code;
        if (newCode !== value) {
          const isNewProduct = prevValueRef.current !== newCode;
          prevValueRef.current = newCode;
          onSelect(mapProduct(match), isNewProduct);
          if (isNewProduct) {
            onProductCodeChange?.(newCode, prevValueRef.current, apiId);
          }
        }
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [q, products, value, apiId]);

  return (
    <div style={{ maxWidth: 150 }}>
      <input
        type="text"
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder="Enter SKU code…"
        className="w-full h-9 border border-slate-200 rounded-lg px-3 text-xs font-mono bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
      />
    </div>
  );
}

function AreaSelect({ value = '', onChange }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="px-2 py-1 rounded-md text-[10px] font-bold border bg-white focus:outline-none"
      style={{ minWidth: 100 }}
    >
      <option value="">Select Area</option>
      {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
    </select>
  );
}

function QtyInput({ value, onChange }) {
  const [localVal, setLocalVal] = useState(value);

  useEffect(() => {
    setLocalVal(value);
  }, [value]);

  const update = (newVal) => {
    setLocalVal(newVal);
    onChange(newVal);
  };

  return (
    <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden bg-white w-[130px] h-9 mx-auto">
      <button
        onClick={() => update(Math.max(1, Number(localVal) - 1))}
        className="w-9 h-full flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-50 flex-shrink-0 border-r border-slate-200 transition-colors"
      >
        <ChevronDown size={12} />
      </button>
      <input
        type="number"
        min={1}
        value={localVal}
        onChange={e => update(e.target.value)}
        className="w-14 h-full text-center text-sm font-bold text-slate-800 focus:outline-none bg-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      <button
        onClick={() => update(Number(localVal) + 1)}
        className="w-9 h-full flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-50 flex-shrink-0 border-l border-slate-200 transition-colors"
      >
        <ChevronUp size={12} />
      </button>
    </div>
  );
}

function NumInput({ value, disabled, onChange, prefix }) {
  const [draft, setDraft] = useState(() => {
    const n = Number(value);
    return isNaN(n) ? '0' : String(n);
  });
  const focusedRef = useRef(false);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!focusedRef.current) {
      const n = Number(value);
      setDraft(isNaN(n) ? '0' : String(n));
    }
  }, [value]);

  const handleFocus = () => {
    focusedRef.current = true;
    const n = Number(value);
    setDraft(isNaN(n) ? '0' : String(n));
    requestAnimationFrame(() => inputRef.current?.select());
  };

  const handleChange = (e) => {
    const raw = e.target.value;
    if (raw === '' || /^-?\d*\.?\d*$/.test(raw)) {
      setDraft(raw);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const n = Number(raw);
        if (!isNaN(n)) onChange(n);
      }, 600);
    }
  };

  const handleBlur = () => {
    focusedRef.current = false;
    clearTimeout(debounceRef.current);
    const n = Number(draft);
    const resolved = isNaN(n) ? 0 : n;
    setDraft(String(resolved));
    onChange(resolved);
  };

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const displayValue = disabled
    ? (Number(value) === 0 ? '0' : String(Number(value)))
    : draft;

  return (
    <div
      className={`flex items-center border rounded-lg overflow-hidden h-9 transition-all
        ${disabled
          ? 'bg-slate-50 border-slate-100'
          : 'bg-white border-slate-200 focus-within:ring-2 focus-within:ring-indigo-400 focus-within:border-transparent'
        }`}
      style={{ width: 120 }}
      onClick={(e) => {
        if (!disabled && inputRef.current) {
          e.stopPropagation();
          inputRef.current.focus();
        }
      }}
    >
      {prefix && (
        <span className={`pl-2.5 pr-1 text-[11px] font-bold select-none flex-shrink-0
          ${disabled ? 'text-slate-300' : 'text-slate-400'}`}>
          {prefix}
        </span>
      )}
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        readOnly={disabled}
        value={displayValue}
        onFocus={disabled ? undefined : handleFocus}
        onChange={disabled ? undefined : handleChange}
        onBlur={disabled ? undefined : handleBlur}
        className={`flex-1 h-full pr-2.5 text-right text-sm font-semibold focus:outline-none bg-transparent min-w-0
          ${disabled ? 'text-slate-400 cursor-default select-none' : 'text-slate-800'}`}
      />
    </div>
  );
}

function Checkbox({ checked, indeterminate, onChange }) {
  const ref = useRef();
  useEffect(() => { if (ref.current) ref.current.indeterminate = !!indeterminate; }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="w-4 h-4 rounded border-slate-300 text-blue-600 cursor-pointer accent-blue-600"
    />
  );
}

function RowActions({ onInsert, onDelete }) {
  return (
    <div className="flex items-center gap-1">
      <button
        title="Insert row below"
        onClick={onInsert}
        className="w-[26px] h-[26px] flex-shrink-0 flex items-center justify-center rounded-md
          border border-blue-200 bg-blue-50 text-blue-500
          hover:bg-blue-100 hover:border-blue-400 hover:text-blue-700
          active:scale-95 transition-all duration-100"
      >
        <Plus size={13} strokeWidth={2.4} />
      </button>
      <button
        title="Delete row"
        onClick={onDelete}
        className="w-[26px] h-[26px] flex-shrink-0 flex items-center justify-center rounded-md
          border border-red-200 bg-red-50 text-red-400
          hover:bg-red-100 hover:border-red-400 hover:text-red-600
          active:scale-95 transition-all duration-100"
      >
        <Trash2 size={12} strokeWidth={2.2} />
      </button>
    </div>
  );
}

function nextSortDir(current) {
  if (!current) return 'desc';
  if (current === 'desc') return 'asc';
  return false;
}

function rowBgClass(isSel, stockZero, stockLow, isEven) {
  if (isSel) return 'bg-blue-100/80';
  if (stockZero) return 'bg-red-50';
  if (stockLow) return 'bg-yellow-50';
  return isEven ? 'bg-white' : 'bg-slate-50/60';
}

function getStatusBadgeStyle(status) {
  switch (status) {
    case 'Active':
      return 'bg-gradient-to-tr from-emerald-600 via-emerald-500 to-teal-400 text-white shadow-lg shadow-emerald-500/50 ring-2 ring-emerald-300/80 animate-pulse';
    case 'Pruning':
      return 'bg-gradient-to-tr from-amber-600 via-amber-500 to-yellow-400 text-white shadow-lg shadow-amber-500/50 ring-2 ring-amber-300/80 animate-pulse';
    case 'Pruned':
      return 'bg-gradient-to-tr from-rose-600 via-rose-500 to-red-400 text-white shadow-lg shadow-rose-500/60 ring-2 ring-rose-300/90 animate-bounce';
    default:
      return 'bg-gradient-to-tr from-slate-700 to-slate-600 text-slate-100 ring-2 ring-slate-400/40';
  }
}

function DraggableRow({ row, ri, selectedIds, onInsertAfter, onDelete, onUpdate }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row.original.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 100 : 'auto',
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
  };

  const isSel = selectedIds.includes(row.original.id);
  const stock = Number(row.original.stock) || 0;
  const qty = Number(row.original.quantity) || 0;
  const hasCode = !!row.original.productCode;
  const isEven = ri % 2 === 0;
  const stockZero = hasCode && stock === 0;
  const stockLow = hasCode && stock < qty && stock > 0;
  const status = row.original?.status || '';

  const bg = rowBgClass(isSel, stockZero, stockLow, isEven);
  const stickyBg = isSel
    ? 'bg-blue-100/80'
    : stockZero ? 'bg-red-50'
      : stockLow ? 'bg-yellow-50'
        : isEven ? 'bg-white'
          : 'bg-slate-50';

  return (
    <tr
      ref={setNodeRef}
      style={style}
      id={`sku-${row.original?.id}`}
      className={`border-b transition-colors group ${bg} ${isDragging ? 'shadow-lg' : ''}`}
    >
      {/* ── Column 1: Drag handle + row number (sticky left: 0) ── */}
      <td
        {...attributes}
        {...listeners}
        className={`px-4 py-2.5 align-middle cursor-grab active:cursor-grabbing
          border-r border-slate-200 ${stickyBg}`}
        style={{
          width: COL_W.drag,
          minWidth: COL_W.drag,
          maxWidth: COL_W.drag,
          position: 'sticky',
          left: 0,
          zIndex: 11,
        }}
      >
        <div className="flex items-center gap-2">
          <GripVertical size={14} className="text-slate-300 group-hover:text-slate-600 transition-colors flex-shrink-0" />
          <div className="relative flex items-center justify-center">
            <span
              className={`absolute inset-0 rounded-lg opacity-75 animate-ping duration-1000 ${status === 'Active'
                ? 'bg-emerald-400'
                : status === 'Pruned'
                  ? 'bg-rose-500'
                  : status === 'Pruning'
                    ? 'bg-amber-400'
                    : 'hidden'
                }`}
            />

            {/* Foreground Badge */}
            <span
              title={`Status: ${status || 'N/A'}`}
              className={`relative min-w-[20px] h-[20px] px-1.5 font-black text-[11px] tracking-tight flex items-center justify-center rounded-lg select-none transition-transform duration-200 group-hover:scale-110 ${getStatusBadgeStyle(status)}`}
            >
              {ri + 1}
            </span>
          </div>
        </div>
      </td>

      {/* ── Remaining columns ── */}
      {row.getVisibleCells().map((cell) => {
        const colId = cell.column.id;
        const isLeftSticky = colId === 'select';
        // stock and stockIndicator are NO LONGER sticky — removed sticky right logic

        return (
          <td
            key={cell.id}
            style={{
              width: cell.column.getSize(),
              minWidth: cell.column.getSize(),
              maxWidth: cell.column.getSize(),
              ...(isLeftSticky
                ? { position: 'sticky', left: COL_W.drag, zIndex: 12 }
                : {}),
            }}
            className={`px-4 py-2.5 align-middle overflow-hidden
              ${isLeftSticky
                ? `border-r border-slate-200 shadow-[4px_0_8px_rgba(0,0,0,0.05)] ${stickyBg}`
                : ''
              }`}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        );
      })}
    </tr>
  );
};

const HoverThumbnailPopup = ({ imageSrc, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <Tooltip
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      title={open && imageSrc ? <img src={imageSrc} alt="Enlarged" style={{ width: '250px', height: '250px', objectFit: 'contain', backgroundColor: 'white' }} /> : ''}
      placement="right"
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: 'white',
            border: '1px solid #e2e8f0',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            padding: '12px',
            maxWidth: '300px'
          }
        },
        arrow: {
          sx: { color: 'white' }
        }
      }}
    >
      {children}
    </Tooltip>
  );
};

const fallbackImage = Apiurl.replace('/api/', '/img/no_image.jpeg');

const StaggeredImage = ({ src, alt, className }) => {
  const [shouldLoad, setShouldLoad] = useState(false);
  const [hasError, setHasError] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Load immediately when it comes within 200px of the viewport
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setShouldLoad(true);
        observer.disconnect();
      }
    }, { rootMargin: '200px' });

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="w-full h-full flex items-center justify-center relative">
      {!shouldLoad && <ImageIcon size={14} className="animate-pulse text-slate-300 absolute" />}
      {shouldLoad && (
        <img
          src={hasError ? fallbackImage : (src || fallbackImage)}
          loading="lazy"
          alt={alt}
          className={className}
          onError={(e) => {
            if (!hasError) setHasError(true);
            else e.target.style.display = 'none';
          }}
        />
      )}
    </div>
  );
};

const DescriptionCell = ({ value, skuCode }) => {
  const [isHovered, setIsHovered] = useState(false);
  const hoverTimeout = useRef(null);

  const handleMouseEnter = () => {
    clearTimeout(hoverTimeout.current);
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => setIsHovered(false), 300);
  };

  return (
    <div
      className="relative cursor-pointer"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <p
        className="text-[11.5px] font-medium text-slate-700 leading-snug line-clamp-2"
        title={value}
      >
        {value || <span className="text-slate-300">—</span>}
      </p>
      {skuCode && isHovered && (
        <div
          className="absolute left-1/4 top-full mt-2 z-50 animate-in fade-in zoom-in duration-200"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <InventoryPopUp sku={skuCode} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} />
        </div>
      )}
    </div>
  );
};

function SortableItem({ id, index, label, isHidden }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: isHidden,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative flex flex-col justify-between p-3 rounded-xl border transition-all select-none min-h-[76px] ${isHidden
        ? 'bg-slate-50/70 border-dashed border-slate-200 opacity-50 backdrop-blur-[2px] cursor-not-allowed'
        : isDragging
          ? 'bg-blue-50/90 border-blue-500 shadow-xl ring-2 ring-blue-400/40 scale-105 z-50'
          : 'bg-white border-slate-200/80 hover:border-blue-300 hover:shadow-md hover:-translate-y-0.5'
        }`}
    >
      <div className="flex items-center justify-between w-full">
        <span
          className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-md ${isHidden
            ? 'bg-slate-200 text-slate-400'
            : 'bg-slate-100 text-slate-500 group-hover:bg-blue-100 group-hover:text-blue-600'
            }`}
        >
          #{String(index + 1).padStart(2, '0')}
        </span>

        {isHidden ? (
          <span className="flex items-center gap-1 text-[9px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
            <EyeOff size={10} /> Hidden
          </span>
        ) : (
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="p-1 -mr-1 text-slate-300 hover:text-blue-600 group-hover:text-slate-500 rounded-md cursor-grab active:cursor-grabbing hover:bg-blue-50 transition-colors touch-none"
            title="Drag card to reorder"
          >
            <GripVertical size={14} />
          </button>
        )}
      </div>

      <div className="mt-2">
        <p
          className={`text-[12px] font-bold tracking-tight truncate ${isHidden ? 'text-slate-400 line-through decoration-slate-300' : 'text-slate-800'
            }`}
          title={label}
        >
          {label}
        </p>
      </div>
    </div>
  );
}

function ColumnOrderModal({ isOpen, onClose, columnOrder, onOrderChange, onReset, columnVisibility = {} }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  if (!isOpen) return null;

  const orderableCols = columnOrder.filter((id) => id !== 'select');

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = orderableCols.indexOf(active.id);
      const newIndex = orderableCols.indexOf(over.id);
      const reordered = arrayMove(orderableCols, oldIndex, newIndex);
      onOrderChange(['select', ...reordered]);
    }
  };

  const getLabel = (id) => TOGGLEABLE_COLUMNS.find((c) => c.id === id)?.label || id;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[85vh] flex flex-col border border-slate-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 border border-blue-100">
              <SlidersHorizontal size={15} />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900">Customize Column Grid</h3>
              <p className="text-[11px] text-slate-500 font-medium">
                Drag cards to set layout.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 flex-1 overflow-y-auto">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={orderableCols} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {orderableCols.map((colId, index) => {
                  const isHidden = columnVisibility[colId] === false;
                  return (
                    <SortableItem
                      key={colId}
                      id={colId}
                      index={index}
                      label={getLabel(colId)}
                      isHidden={isHidden}
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        </div>

        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/70 flex justify-between items-center">
          <button
            type="button"
            onClick={onReset}
            className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors"
          >
            <RotateCcw size={13} /> Reset Order
          </button>

        </div>
      </div>
    </div>
  );
}

const columnHelper = createColumnHelper();

export default function QuotationTable({
  data = [],
  selections = [],
  currentSelection = null,
  onUpdate,
  onDelete, onInsertAfter, onDeleteSelected, onProductCodeChange,
  discountType, gstType,
  sorting = [],
  onSortingChange,
  onSmartSort,
  onReorder,
  defaultQty,
  defaultDiscountPercent,
  onAddProducts,
  activeSkuForSuggestion,
  setActiveSkuForSuggestion,
}) {

  const { masterProduct = [] } = useSelector(s => s.product);
  const { showSmartSuggestions = true, showLowStockWarning = true } = useSelector(s => s.pdfImages || {});
  const [selectedIds, setSelectedIds] = useState([]);

  const allSelected = data.length > 0 && selectedIds.length === data.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < data.length;
  const [activeSku, setActiveSku] = useState(null);
  const hoverTimeout = useRef(null);
  const scrollContainerRef = useRef(null);
  // Auto-scroll logic removed entirely to test if this is the culprit.

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );



  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = data.findIndex((item) => item.id === active.id);
      const newIndex = data.findIndex((item) => item.id === over.id);
      onReorder?.(arrayMove(data, oldIndex, newIndex));
    }
  };

  const defaultQtyRef = useRef(defaultQty);
  const defaultDiscountRef = useRef(defaultDiscountPercent);
  useEffect(() => { defaultQtyRef.current = defaultQty; }, [defaultQty]);
  useEffect(() => { defaultDiscountRef.current = defaultDiscountPercent; }, [defaultDiscountPercent]);

  const dataRef = useRef(data);
  useEffect(() => { dataRef.current = data; }, [data]);

  const toggleAll = () =>
    setSelectedIds(prev =>
      prev.length === dataRef.current.length ? [] : dataRef.current.map(r => r.id)
    );

  const toggleRow = (id) => setSelectedIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const handleBulkDelete = () => { onDeleteSelected(selectedIds); setSelectedIds([]); };

  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);

  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('table_col_visibility');
    if (saved) {
      try { return JSON.parse(saved); } catch (e) { /* fallback */ }
    }
    return TOGGLEABLE_COLUMNS.reduce((acc, col) => ({ ...acc, [col.id]: true }), {});
  });

  const [columnOrder, setColumnOrder] = useState(() => {
    const saved = localStorage.getItem('table_col_order');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const missing = DEFAULT_COLUMN_ORDER.filter(id => !parsed.includes(id));
        return [...parsed, ...missing];
      } catch (e) { console.error(e); }
    }
    return DEFAULT_COLUMN_ORDER;
  });

  const handleColumnOrderChange = useCallback((newOrder) => {
    setColumnOrder(newOrder);
    localStorage.setItem('table_col_order', JSON.stringify(newOrder));
  }, []);

  const handleResetColumnOrder = useCallback(() => {
    setColumnOrder(DEFAULT_COLUMN_ORDER);
    localStorage.removeItem('table_col_order');
  }, []);

  const handleToggleColumn = useCallback((colId) => {
    setColumnVisibility((prev) => {
      const updated = { ...prev, [colId]: prev[colId] === false ? true : false };
      localStorage.setItem('table_col_visibility', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const handleProductSelect = useCallback((rowId, product, isNewProduct) => {
    ['productCode', 'brand', 'description', 'collection', 'productType', 'category',
      'priceSegment', 'finish', 'mrp', 'basePrice', 'gstRate', 'preferences', 'stock', 'stockIndicator', 'image', 'linediagram_image', 'pruning_date', 'status']
      .forEach(f => onUpdate(rowId, f, product[f]));

    if (isNewProduct) {
      onUpdate(rowId, 'quantity', defaultQtyRef.current);
      if (discountType === 'price') {
        const priceBase = gstType === 'INC' ? Number(product.mrp) || 0 : Number(product.basePrice) || 0;
        const discountPrice = Math.round(priceBase * (defaultDiscountRef.current / 100) * 100) / 100;
        onUpdate(rowId, 'discountPrice', discountPrice);
      } else {
        onUpdate(rowId, 'discountPercent', defaultDiscountRef.current);
      }
      if (product.productCode) setActiveSkuForSuggestion(product.productCode);
    }
  }, [onUpdate, discountType, gstType]);

  const handleSort = useCallback((colId) => {
    const existing = sorting.find(s => s.id === colId);
    const current = existing ? (existing.desc ? 'desc' : 'asc') : false;
    const next = nextSortDir(current);
    const newSorting = !next
      ? sorting.filter(s => s.id !== colId)
      : [{ id: colId, desc: next === 'desc' }];
    onSortingChange?.(newSorting);
  }, [sorting, onSortingChange]);

  const getSortState = (colId) => {
    const s = sorting.find(s => s.id === colId);
    if (!s) return false;
    return s.desc ? 'desc' : 'asc';
  };

  const columns = useMemo(() => [
    // ── select + row-actions (sticky left: COL_W.drag) ──────────────────────
    columnHelper.display({
      id: 'select',
      size: COL_W.select,
      enableSorting: false,
      meta: { stickyLeft: true },
      header: () => (
        <Checkbox checked={allSelected} indeterminate={someSelected} onChange={toggleAll} />
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Checkbox
            checked={selectedIds.includes(row.original.id)}
            onChange={() => toggleRow(row.original.id)}
          />
          <RowActions
            onInsert={() => onInsertAfter(row.original.id)}
            onDelete={() => onDelete(row.original.id)}
          />
        </div>
      ),
    }),

    columnHelper.accessor('productCode', {
      header: 'Product Code', size: COL_W.productCode, enableSorting: true,
      cell: ({ row }) => (
        <CodeInput
          value={row.original.productCode}
          apiId={row.original.apiId}
          onSelect={(p, isNewProduct) => handleProductSelect(row.original.id, p, isNewProduct)}
          onProductCodeChange={(newCode, oldCode, apiId) =>
            onProductCodeChange?.(row.original.id, apiId, oldCode, newCode)
          }
          products={masterProduct}
        />
      ),
    }),

    columnHelper.accessor('area', {
      header: 'Area', size: COL_W.area, enableSorting: false,
      cell: ({ row }) => (
        <AreaSelect value={row.original.area || ''} onChange={v => onUpdate(row.original.id, 'area', v)} />
      ),
    }),

    columnHelper.accessor('brand', {
      header: 'Brand', size: COL_W.brand, enableSorting: true,
      cell: ({ getValue }) => {
        const v = getValue();
        return v
          ? <span className="inline-block text-[11px] font-extrabold text-slate-700 bg-slate-900/[0.06] px-2.5 py-1 rounded-lg whitespace-nowrap tracking-wide">{v}</span>
          : <span className="text-slate-300">—</span>;
      },
    }),

    columnHelper.accessor('description', {
      header: 'Description', size: COL_W.description, enableSorting: true,
      cell: ({ getValue, row }) => (
        <DescriptionCell value={getValue()} skuCode={row.original.productCode || row.original.sku_code} />
      ),
    }),

    columnHelper.accessor('collection', {
      header: 'Collection', size: COL_W.collection, enableSorting: true,
      cell: ({ getValue }) => <span className="text-[11px] text-slate-500">{getValue() || '—'}</span>,
    }),

    // columnHelper.accessor('productType', {
    //   header: 'Type', size: COL_W.productType, enableSorting: true,
    //   cell: ({ getValue }) => <span className="text-[11px] text-slate-500 whitespace-nowrap">{getValue() || '—'}</span>,
    // }),

    columnHelper.accessor('category', {
      header: 'Category', size: COL_W.category, enableSorting: true,
      cell: ({ getValue }) => <span className="text-[11px] text-slate-500">{getValue() || '—'}</span>,
    }),

    columnHelper.accessor('priceSegment', {
      header: 'Price Segment', size: COL_W.priceSegment, enableSorting: true,
      cell: ({ getValue }) => (
        <span className="text-[12px] font-medium text-slate-600 block text-center">{getValue() || '-'}</span>
      ),
    }),

    columnHelper.accessor('finish', {
      header: 'Finish', size: COL_W.finish, enableSorting: true,
      cell: ({ getValue }) => <span className="text-[11px] text-slate-500 whitespace-nowrap">{getValue() || '-'}</span>,
    }),

    columnHelper.display({
      id: 'image', header: 'Image', size: COL_W.image, enableSorting: false,
      cell: ({ row }) => {
        const imageSrc = row.original.image || fallbackImage;
        return (
          <HoverThumbnailPopup imageSrc={imageSrc}>
            <div className="w-9 h-9 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center text-slate-300 mx-auto overflow-hidden cursor-pointer shadow-sm rounded transition-colors hover:bg-slate-200">
              <StaggeredImage src={imageSrc} alt="" index={row.index} className="w-full h-full object-cover transition-transform duration-300 hover:scale-110" />
            </div>
          </HoverThumbnailPopup>
        );
      },
    }),
    columnHelper.display({
      id: 'linediagram_image', header: 'Line Diagram', size: COL_W.linediagram_image, enableSorting: false,
      cell: ({ row }) => {
        const linediagram_imageSrc = row.original.linediagram_image || fallbackImage;
        return (
          <HoverThumbnailPopup imageSrc={linediagram_imageSrc}>
            <div className="w-9 h-9 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center text-slate-300 mx-auto overflow-hidden cursor-pointer shadow-sm rounded transition-colors hover:bg-slate-200">
              <StaggeredImage src={linediagram_imageSrc} alt="" index={row.index} className="w-full h-full object-cover transition-transform duration-300 hover:scale-110" />
            </div>
          </HoverThumbnailPopup>
        );
      },
    }),

    columnHelper.accessor('quantity', {
      header: "Qty",
      size: COL_W.quantity,
      enableSorting: true,
      cell: ({ row }) => (
        <div className="flex justify-center">
          <QtyInput value={row.original.quantity} onChange={v => onUpdate(row.original.id, 'quantity', v)} />
        </div>
      ),
    }),

    columnHelper.accessor('mrp', {
      header: 'MRP (incl. GST)', size: COL_W.mrp, enableSorting: true,
      cell: ({ getValue }) => (
        <span className="text-[12px] font-bold text-slate-800 whitespace-nowrap block">{formatCurrency(getValue())}</span>
      ),
    }),

    columnHelper.accessor('basePrice', {
      header: 'Base Price (excl. GST)', size: COL_W.basePrice, enableSorting: true,
      cell: ({ getValue }) => (
        <span className="text-[11px] text-slate-400 whitespace-nowrap block">{formatCurrency(getValue())}</span>
      ),
    }),

    columnHelper.accessor('discountPercent', {
      header: 'Disc %', size: COL_W.discountPercent, enableSorting: true,
      cell: ({ row }) => (
        <NumInput
          value={row.original.discountPercent}
          disabled={discountType !== 'percent'}
          onChange={v => onUpdate(row.original.id, 'discountPercent', v)}
          prefix="%"
        />
      ),
    }),

    // columnHelper.accessor('discountPrice', {
    //   header: 'Disc (₹/unit)', size: COL_W.discountPrice, enableSorting: true,
    //   cell: ({ row }) => (
    //     <NumInput
    //       value={row.original.discountPrice}
    //       disabled={discountType !== 'price'}
    //       onChange={v => onUpdate(row.original.id, 'discountPrice', v)}
    //       prefix="₹"
    //     />
    //   ),
    // }),

    columnHelper.accessor('discountPrice', {
      header: `Disc (${getCurrencySymbol()}/unit)`, size: COL_W.discountPrice, enableSorting: true,
      cell: ({ row }) => {
        const currentCurrency = getSelectedCurrency();
        const exchangeRate = currentCurrency === 'INR' ? 1 : (convertAmount(1) / 1); 
        const displayedDiscountPrice = currentCurrency === 'INR'
          ? row.original.discountPrice
          : Number((row.original.discountPrice * exchangeRate).toFixed(2));

        return (
          <NumInput
            value={displayedDiscountPrice}
            disabled={discountType !== 'price'}
            onChange={v => {
              const baseINRValue = currentCurrency === 'INR' ? v : v / exchangeRate;
              onUpdate(row.original.id, 'discountPrice', R2(baseINRValue));
            }}
            prefix={getCurrencySymbol()}
          />
        );
      },
    }),

    columnHelper.accessor('gstRate', {
      header: 'GST %', size: COL_W.gstRate, enableSorting: true,
      cell: ({ getValue }) => (
        <div className="text-left">
          <span className="text-[12px] font-bold text-slate-500">{gstType === 'INC' ? getValue() : '0'}%</span>
        </div>
      ),
    }),

    columnHelper.accessor('totalDiscount', {
      header: 'Total Disc', size: COL_W.totalDiscount, enableSorting: true,
      cell: ({ getValue }) => (
        <span className="text-[12px] font-semibold text-rose-500 whitespace-nowrap block text-left">
          −{formatCurrency(getValue())}
        </span>
      ),
    }),

    columnHelper.accessor('total', {
      header: 'Total', size: COL_W.total, enableSorting: true,
      cell: ({ getValue }) => (
        <span className="text-[13px] font-extrabold text-blue-600 whitespace-nowrap block text-left">
          {formatCurrency(getValue())}
        </span>
      ),
    }),

    columnHelper.accessor('preferences', {
      header: 'Preferences', size: COL_W.preferences, enableSorting: true,
      cell: ({ getValue }) => (
        <span className="text-[12px] font-medium text-slate-600 block text-left">{getValue()}</span>
      ),
    }),

    columnHelper.accessor('stock', {
      header: 'Stock', size: COL_W.stock, enableSorting: true,
      cell: ({ getValue }) => (
        <span className="text-[12px] font-medium text-slate-600 block text-left">{getValue()}</span>
      ),
    }),

    columnHelper.accessor('stockIndicator', {
      header: 'Stock Indicator', size: COL_W.stockIndicator, enableSorting: true,
      cell: ({ getValue }) => (
        <span className="text-[12px] font-medium text-slate-600 block text-left">{getValue()}</span>
      ),
    }),

    columnHelper.accessor('status', {
      header: 'Status', size: COL_W.status, enableSorting: true,
      cell: ({ getValue }) => (
        <span className="text-[12px] font-medium text-slate-600 block text-left">{getValue()}</span>
      ),
    }),

    columnHelper.accessor('pruning_date', {
      header: 'Pruning Date', size: COL_W.pruning_date, enableSorting: true,
      cell: ({ getValue }) => (
        <span className="text-[12px] font-medium text-slate-600 block text-left">{getValue()}</span>
      ),
    }),
  ], [discountType, gstType, selectedIds, allSelected, someSelected, masterProduct, onUpdate, onInsertAfter, onDelete, onProductCodeChange]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting, columnVisibility, columnOrder },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: handleColumnOrderChange,
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      onSortingChange?.(newSorting);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: false,
  });

  const grandTotal = R2(data.reduce((s, r) => s + r.total, 0));
  const grandDiscount = R2(data.reduce((s, r) => s + r.totalDiscount, 0));
  const grandQty = data.reduce((s, r) => s + (Number(r.quantity) || 0), 0);
  const productCount = data.filter(r => r.productCode).length;

  return (
    <>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans', system-ui, sans-serif", position: 'relative' }}>

        {selectedIds.length > 0 && (
          <div style={{ flexShrink: 0 }} className="px-5 py-2 bg-blue-700 flex items-center gap-3">
            <span className="text-xs font-bold text-blue-100">
              {selectedIds.length} row{selectedIds.length !== 1 ? 's' : ''} selected
            </span>
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
            >
              <Trash2 size={12} /> Delete
            </button>
            <button
              onClick={() => setSelectedIds([])}
              className="text-xs font-medium text-blue-300 hover:text-white transition-colors"
            >
              Clear
            </button>
          </div>
        )}

        <div
          ref={scrollContainerRef}
          style={{
            flex: 1,
            overflowX: 'scroll',
            overflowY: 'auto',
            position: 'relative',
            marginBottom: '0px'
          }}
          className="bg-slate-100"
        >
          <table className="border-collapse" style={{ width: 'max-content', minWidth: '100%' }}>

            <thead style={{ position: 'sticky', top: 0, zIndex: 20 }}>
              {table.getHeaderGroups().map(hg => (
                <tr key={hg.id}>

                  {/* ── Header: # / drag handle ── */}
                  <th
                    className="px-4 py-3 text-left text-[10px] font-extrabold uppercase tracking-widest
                      text-slate-400 bg-slate-900 border-b-2 border-r border-slate-700"
                    style={{
                      width: COL_W.drag,
                      minWidth: COL_W.drag,
                      position: 'sticky',
                      left: 0,
                      zIndex: 32,
                    }}
                  >
                    #
                  </th>

                  {hg.headers.map((header) => {
                    const canSort = header.column.columnDef.enableSorting !== false;
                    const sortState = getSortState(header.column.id);
                    const isLeftSticky = header.column.columnDef.meta?.stickyLeft === true;
                    // No right-sticky headers any more

                    return (
                      <th
                        key={header.id}
                        style={{
                          width: header.getSize(),
                          minWidth: header.getSize(),
                          ...(isLeftSticky
                            ? { position: 'sticky', left: COL_W.drag, zIndex: 31 }
                            : {}),
                        }}
                        onClick={() => canSort && handleSort(header.column.id)}
                        className={`
                          px-4 py-3 text-left text-[10px] font-extrabold uppercase tracking-widest whitespace-nowrap select-none
                          border-b-2 border-slate-700
                          ${canSort ? 'cursor-pointer group' : ''}
                          ${isLeftSticky ? 'border-r border-slate-700 shadow-[4px_0_8px_rgba(0,0,0,0.25)]' : ''}
                          ${sortState ? 'text-blue-400 bg-slate-800' : 'text-slate-400 bg-slate-900'}
                        `}
                      >
                        <span className="inline-flex items-center">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort && <SortIcon state={sortState} />}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={data.map(d => d.id)}
                strategy={verticalListSortingStrategy}
              >
                <tbody>
                  {table.getRowModel().rows.map((row, ri) => (
                    <DraggableRow
                      key={row.id}
                      row={row}
                      ri={ri}
                      selectedIds={selectedIds}
                      onUpdate={onUpdate}
                      onDelete={onDelete}
                      onInsertAfter={onInsertAfter}
                    />
                  ))}

                  {data.length === 0 && (
                    <tr>
                      <td colSpan={columns.length + 1} className="py-20 text-center">
                        <div className="flex flex-col items-center gap-3 text-slate-400">
                          <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center">
                            <Package size={28} className="opacity-40" />
                          </div>
                          <p className="text-sm font-semibold">No items yet</p>
                          <p className="text-xs">Add products via catalog, code search, or Excel import</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </SortableContext>
            </DndContext>
          </table>
        </div>

        {/* ── Footer ── */}
        <div
          style={{ flexShrink: 0 }}
          className="shadow-[0_-8px_20px_rgba(0,0,0,0.15)] flex flex-col z-30 relative"
        >
          <div className="bg-gray-900/90 backdrop-blur-md border-t border-white/20 px-5 h-[56px] flex items-center gap-0 overflow-x-auto">
            <div className="flex items-center gap-3 flex-shrink-0 pr-6 border-r border-white/20 mr-6">
              <span className="text-[10px] font-extrabold text-slate-300 uppercase tracking-widest whitespace-nowrap">Grand Total</span>
              <span className="text-[10px] text-slate-200 font-medium whitespace-nowrap">
                {productCount} product{productCount !== 1 ? 's' : ''}
              </span>
            </div>

            <div className="flex flex-col items-center px-5 border-r border-white/20 flex-shrink-0">
              <span className="text-[9px] font-extrabold text-slate-300 uppercase tracking-widest mb-0.5">Total Qty</span>
              <span className="text-sm font-extrabold text-white">{grandQty.toLocaleString('en-IN')}</span>
            </div>

            <div className="flex flex-col items-center px-5 flex-shrink-0">
              <span className="text-[9px] font-extrabold text-slate-300 uppercase tracking-widest mb-0.5">
                {gstType === 'INC' ? 'List Price (incl. GST)' : 'List Price (excl. GST)'}
              </span>
              <span className="text-base font-extrabold text-white tracking-tight">{formatCurrency(grandTotal + grandDiscount)}</span>
            </div>

            <div className="flex flex-col items-center px-5 border-r border-white/20 flex-shrink-0">
              <span className="text-[9px] font-extrabold text-slate-300 uppercase tracking-widest mb-0.5">Total Discount</span>
              <span className="text-sm font-extrabold text-rose-400">-{formatCurrency(grandDiscount)}</span>
            </div>

            <div className="flex flex-col items-center px-5 flex-shrink-0">
              <span className="text-[9px] font-extrabold text-slate-300 uppercase tracking-widest mb-0.5">
                {gstType === 'INC' ? 'Offer Price (incl. GST)' : 'Offer Price (excl. GST)'}
              </span>
              <span className="text-base font-extrabold text-white tracking-tight">{formatCurrency(grandTotal)}</span>
            </div>

            <div className="flex-1" />

            {/* Inline Stock Warning Indicator */}
            <div className="flex-shrink-0 mr-4">
              {showLowStockWarning && <StockLable data={selections} currentSelection={currentSelection} />}
            </div>

            {/* Inline Smart Suggestions Trigger */}
            <div className="flex-shrink-0 mr-4">
              {showSmartSuggestions && (
                <SmartAutoRecommendations
                  activeSkuForSuggestion={activeSkuForSuggestion}
                  setActiveSkuForSuggestion={setActiveSkuForSuggestion}
                  selections={[{ rows: data }]}
                  masterProduct={masterProduct}
                  onAddProducts={onAddProducts}
                />
              )}
            </div>

            <div className={`flex-shrink-0 px-3 py-1 rounded-lg text-[10px] font-extrabold uppercase tracking-wider border
            ${gstType === 'INC'
                ? 'bg-blue-900/50 text-blue-300 border-blue-700'
                : 'bg-orange-900/50 text-orange-300 border-orange-700'}`}>
              GST {gstType === 'INC' ? 'Inclusive' : 'Exclusive'}
            </div>
          </div>

          <div className="bg-slate-100 border-t border-slate-300 px-4 h-[40px] flex items-center gap-4 overflow-x-auto no-scrollbar select-none">
            <button
              type="button"
              onClick={() => setIsOrderModalOpen(true)}
              className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md flex-shrink-0 transition-colors"
            >
              <SlidersHorizontal size={12} />
              Order Columns
            </button>

            <div className="w-px h-4 bg-slate-300 flex-shrink-0" />
            {TOGGLEABLE_COLUMNS.map((col) => {
              const isVisible = table.getColumn(col.id)?.getIsVisible() ?? true;
              return (
                <label
                  key={col.id}
                  className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-700 hover:text-blue-600 cursor-pointer flex-shrink-0 whitespace-nowrap"
                >
                  <input
                    type="checkbox"
                    checked={isVisible}
                    onChange={() => handleToggleColumn(col.id)}
                    className="w-3.5 h-3.5 rounded border-slate-300 text-blue-600 focus:ring-0 accent-blue-600 cursor-pointer"
                  />
                  {col.label}
                </label>
              );
            })}
          </div>
        </div>

      </div>

      <ColumnOrderModal
        isOpen={isOrderModalOpen}
        onClose={() => setIsOrderModalOpen(false)}
        columnOrder={columnOrder}
        columnVisibility={columnVisibility}
        onOrderChange={handleColumnOrderChange}
        onReset={handleResetColumnOrder}
      />
    </>
  );
}